import React, { useState } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import { LicensesBillingServices } from "../../../../services/organization.service";
import { store } from "react-notifications-component";

const useStyles = makeStyles((theme) => ({
    card: {
        marginBottom: theme.spacing(2),
    },
    title: {
        fontSize: '1rem',
        fontWeight: 500,
    },
    subheader: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    chip: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    stat: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    statValue: {
        fontWeight: 'bold',
        fontSize: '1.2rem',
    },
    statLabel: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
    accordion: {
        marginTop: theme.spacing(2),
    },
    table: {
        minWidth: 650,
    },
    orgColumn: {
        minWidth: 200,
        maxWidth: 250,
    },
    actionColumn: {
        width: 60,
        padding: theme.spacing(0.5),
    },
    statusPending: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
    },
    statusPaid: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    },
    statusUnderReview: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
    },
    pendingCount: {
        marginLeft: theme.spacing(1),
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        fontWeight: 'bold',
    },
    pendingCountHighlight: {
        marginLeft: theme.spacing(1),
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        fontWeight: 'bold',
        animation: '$pulse 2s infinite',
    },
    underReviewCount: {
        marginLeft: theme.spacing(1),
        backgroundColor: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        fontWeight: 'bold',
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
        },
        '50%': {
            transform: 'scale(1.05)',
        },
        '100%': {
            transform: 'scale(1)',
        },
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalPaper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(4),
        margin: theme.spacing(2),
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        borderRadius: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    orgCard: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
    },
    programBreakdownSection: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    programCard: {
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
    },
    programHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    programStats: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    programStat: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 60,
    },
    programStatValue: {
        fontWeight: 'bold',
        fontSize: '1rem',
        color: theme.palette.primary.main,
    },
    programStatLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}));

function InsiderCard({ insider, onDataUpdate, showDQW = false }) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedGroupedRecord, setSelectedGroupedRecord] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

    const handleChange = () => {
        setExpanded(!expanded);
    };

    const handleMenuClick = (event, groupedRecord) => {
        setAnchorEl(event.currentTarget);
        setSelectedGroupedRecord(groupedRecord);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedGroupedRecord(null);
    };

    const handleViewInvoice = () => {
        setSelectedInvoiceData(selectedGroupedRecord);
        setModalOpen(true);
        handleMenuClose();
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedInvoiceData(null);
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!selectedGroupedRecord) return;
        
        setUpdating(true);
        
        try {
            // Update status for all organizations in this month
            const updatePromises = selectedGroupedRecord.records.map(record =>
                LicensesBillingServices.updateStatus(
                    record.org_code,
                    record.year,
                    record.month,
                    newStatus
                )
            );
            
            await Promise.all(updatePromises);
            
            store.addNotification({
                title: "Success",
                message: `Billing status updated for all organizations in ${selectedGroupedRecord.month_name} ${selectedGroupedRecord.year}`,
                type: "success",
                insert: "bottom",
                container: "top-right",
                animationIn: ["animated", "fadeIn"],
                animationOut: ["animated", "fadeOut"],
                dismiss: {
                    duration: 3000,
                    showIcon: true
                }
            });
            
            // Trigger data refresh if callback provided
            if (onDataUpdate) {
                onDataUpdate();
            }
        } catch (error) {
            console.error('Error updating billing status:', error);
            store.addNotification({
                title: "Error",
                message: "Failed to update billing status",
                type: "danger",
                insert: "bottom",
                container: "top-right",
                animationIn: ["animated", "fadeIn"],
                animationOut: ["animated", "fadeOut"],
                dismiss: {
                    duration: 3000,
                    showIcon: true
                }
            });
        } finally {
            setUpdating(false);
            handleMenuClose();
        }
    };

    const getStatusChip = (status) => {
        let statusClass, statusText;
        
        switch (status) {
            case 1:
                statusClass = classes.statusPending;
                statusText = "Pending";
                break;
            case 2:
                statusClass = classes.statusPaid;
                statusText = "Paid";
                break;
            case 3:
                statusClass = classes.statusUnderReview;
                statusText = "Under Review";
                break;
            default:
                statusClass = classes.statusPending;
                statusText = "Unknown";
        }
        
        return (
            <Chip 
                label={statusText} 
                size="small" 
                className={statusClass} 
            />
        );
    };

    if (!insider) {
        return null;
    }

    const { insider_profile, billing_summary, billing_records, organizations } = insider;
    const displayName = insider_profile.display_name || `${insider_profile.first_name} ${insider_profile.last_name}`;
    
    // Group billing records by month and aggregate stats
    const groupedRecords = billing_records ? billing_records.reduce((acc, record) => {
        const key = `${record.year}-${record.month}`;
        
        if (!acc[key]) {
            acc[key] = {
                billing_month: record.billing_month,
                year: record.year,
                month: record.month,
                month_name: record.month_name,
                records: [],
                total_dqp_count: 0,
                total_dqw_count: 0,
                total_users: 0,
                organizations_count: 0,
                // Status priority: 1 (pending) > 3 (under review) > 2 (paid)
                aggregated_status: 2, // Start with paid (lowest priority)
                has_pending: false,
                has_under_review: false
            };
        }
        
        const group = acc[key];
        group.records.push(record);
        group.total_dqp_count += parseInt(record.dqp_count) || 0;
        group.total_dqw_count += parseInt(record.dqw_count) || 0;
        group.total_users += parseInt(record.no_of_users) || 0;
        group.organizations_count += 1;
        
        // Determine aggregated status based on priority
        if (record.status === 1) {
            group.has_pending = true;
            group.aggregated_status = 1; // Pending has highest priority
        } else if (record.status === 3 && group.aggregated_status !== 1) {
            group.has_under_review = true;
            group.aggregated_status = 3; // Under review has medium priority
        }
        
        return acc;
    }, {}) : {};

    const groupedRecordsArray = Object.values(groupedRecords).sort((a, b) => b.billing_month - a.billing_month);

    // Calculate pending and under-review bills count based on grouped records
    const pendingBillsCount = groupedRecordsArray.filter(group => group.aggregated_status === 1).length;
    const underReviewBillsCount = groupedRecordsArray.filter(group => group.aggregated_status === 3).length;

    // Helper function to get organization display name
    const getOrganizationDisplay = (orgCode) => {
        if (!organizations || organizations.length === 0) {
            return orgCode; // fallback to just the code
        }
        
        const org = organizations.find(o => o.org_code === orgCode);
        if (org && org.org_name) {
            return `${org.org_name} (${orgCode})`;
        }
        
        return orgCode; // fallback if org not found
    };

    return (
        <Card className={classes.card}>
            <CardContent>
                <Typography className={classes.title} gutterBottom>
                    {displayName}
                </Typography>
                <Typography className={classes.subheader} gutterBottom>
                    ID: {insider.dq_insider_id} | Type: {insider_profile.profile_type}
                </Typography>
                
                {organizations && organizations.length > 0 && (
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                        {organizations.map((org, index) => (
                            <Chip 
                                key={index}
                                label={`${org.org_name} (${org.org_code})`} 
                                className={classes.chip} 
                                variant="outlined" 
                                size="small"
                            />
                        ))}
                    </div>
                )}

                <Grid container spacing={2} style={{ marginTop: 8 }}>
                    <Grid item xs={4} sm={2}>
                        <div className={classes.stat}>
                            <Typography className={classes.statValue}>
                                {billing_summary.total_organizations}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Organizations
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <div className={classes.stat}>
                            <Typography className={classes.statValue}>
                                {billing_summary.total_billing_records}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Billing Records
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <div className={classes.stat}>
                            <Typography className={classes.statValue}>
                                {billing_summary.total_dqp_count}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                DQP Count
                            </Typography>
                        </div>
                    </Grid>
                    {showDQW && (
                        <Grid item xs={4} sm={2}>
                            <div className={classes.stat}>
                                <Typography className={classes.statValue}>
                                    {billing_summary.total_dqw_count}
                                </Typography>
                                <Typography className={classes.statLabel}>
                                    DQW Count
                                </Typography>
                            </div>
                        </Grid>
                    )}
                    <Grid item xs={4} sm={2}>
                        <div className={classes.stat}>
                            <Typography className={classes.statValue}>
                                {billing_summary.total_users}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Total Users
                            </Typography>
                        </div>
                    </Grid>
                </Grid>

                {groupedRecordsArray && groupedRecordsArray.length > 0 && (
                    <Accordion className={classes.accordion} expanded={expanded} onChange={handleChange}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography>Billing Records ({groupedRecordsArray.length} months)</Typography>
                                {pendingBillsCount > 0 && (
                                    <Chip 
                                        label={`${pendingBillsCount} Pending`}
                                        size="small"
                                        className={pendingBillsCount > 3 ? classes.pendingCountHighlight : classes.pendingCount}
                                    />
                                )}
                                {underReviewBillsCount > 0 && (
                                    <Chip 
                                        label={`${underReviewBillsCount} Under Review`}
                                        size="small"
                                        className={classes.underReviewCount}
                                    />
                                )}
                            </div>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer component={Paper}>
                                <Table className={classes.table} size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Period</TableCell>
                                            <TableCell align="center">Organizations</TableCell>
                                            <TableCell align="right">Total DQP</TableCell>
                                            {showDQW && <TableCell align="right">Total DQW</TableCell>}
                                            <TableCell align="right">Total Users</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center" className={classes.actionColumn}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groupedRecordsArray.map((groupedRecord) => (
                                            <TableRow key={`${groupedRecord.year}-${groupedRecord.month}`}>
                                                <TableCell>{groupedRecord.month_name} {groupedRecord.year}</TableCell>
                                                <TableCell align="center">{groupedRecord.organizations_count}</TableCell>
                                                <TableCell align="right">{groupedRecord.total_dqp_count}</TableCell>
                                                {showDQW && <TableCell align="right">{groupedRecord.total_dqw_count}</TableCell>}
                                                <TableCell align="right">{groupedRecord.total_users}</TableCell>
                                                <TableCell>{getStatusChip(groupedRecord.aggregated_status)}</TableCell>
                                                <TableCell align="center" className={classes.actionColumn}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleMenuClick(e, groupedRecord)}
                                                        disabled={updating}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Status Update Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={handleViewInvoice}>
                        <VisibilityIcon style={{ marginRight: 8 }} />
                        View Invoice
                    </MenuItem>
                    {selectedGroupedRecord && selectedGroupedRecord.aggregated_status === 1 && (
                        <>
                            <MenuItem onClick={() => handleStatusUpdate(2)}>
                                Mark as Paid
                            </MenuItem>
                            <MenuItem onClick={() => handleStatusUpdate(3)}>
                                Mark as Under Review
                            </MenuItem>
                        </>
                    )}
                    {selectedGroupedRecord && selectedGroupedRecord.aggregated_status === 3 && (
                        <MenuItem onClick={() => handleStatusUpdate(2)}>
                            Mark as Paid
                        </MenuItem>
                    )}
                </Menu>

                {/* Invoice Details Modal */}
                <Modal
                    className={classes.modal}
                    open={modalOpen}
                    onClose={handleModalClose}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{
                        timeout: 500,
                    }}
                >
                    <Fade in={modalOpen}>
                        <div className={classes.modalPaper}>
                            <div className={classes.modalHeader}>
                                <Typography variant="h6">
                                    Invoice Details - {selectedInvoiceData?.month_name} {selectedInvoiceData?.year}
                                </Typography>
                                <IconButton onClick={handleModalClose} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </div>
                            
                            {selectedInvoiceData && (
                                <>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Monthly Summary</strong>
                                    </Typography>
                                    <Grid container spacing={2} style={{ marginBottom: 24 }}>
                                        <Grid item xs={showDQW ? 3 : 4}>
                                            <Paper className={classes.orgCard}>
                                                <Typography variant="body2" color="textSecondary">Organizations</Typography>
                                                <Typography variant="h6">{selectedInvoiceData.organizations_count}</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={showDQW ? 3 : 4}>
                                            <Paper className={classes.orgCard}>
                                                <Typography variant="body2" color="textSecondary">Total DQP</Typography>
                                                <Typography variant="h6">{selectedInvoiceData.total_dqp_count}</Typography>
                                            </Paper>
                                        </Grid>
                                        {showDQW && (
                                            <Grid item xs={3}>
                                                <Paper className={classes.orgCard}>
                                                    <Typography variant="body2" color="textSecondary">Total DQW</Typography>
                                                    <Typography variant="h6">{selectedInvoiceData.total_dqw_count}</Typography>
                                                </Paper>
                                            </Grid>
                                        )}
                                        <Grid item xs={showDQW ? 3 : 4}>
                                            <Paper className={classes.orgCard}>
                                                <Typography variant="body2" color="textSecondary">Total Users</Typography>
                                                <Typography variant="h6">{selectedInvoiceData.total_users}</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Divider style={{ margin: '16px 0' }} />

                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Organization Breakdown</strong>
                                    </Typography>
                                    
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Organization</TableCell>
                                                    <TableCell align="right">DQP</TableCell>
                                                    {showDQW && <TableCell align="right">DQW</TableCell>}
                                                    <TableCell align="right">Users</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Last Update</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedInvoiceData.records.map((record) => (
                                                    <TableRow key={record.id}>
                                                        <TableCell>{getOrganizationDisplay(record.org_code)}</TableCell>
                                                        <TableCell align="right">{record.dqp_count}</TableCell>
                                                        {showDQW && <TableCell align="right">{record.dqw_count}</TableCell>}
                                                        <TableCell align="right">{record.no_of_users}</TableCell>
                                                        <TableCell>{getStatusChip(record.status)}</TableCell>
                                                        <TableCell>
                                                            {new Date(record.last_stats_update).toLocaleDateString()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* Program Breakdown Section */}
                                    {selectedInvoiceData.records.some(record => record.program_breakdown && record.program_breakdown.length > 0) && (
                                        <div className={classes.programBreakdownSection}>
                                            <Divider style={{ margin: '24px 0 16px 0' }} />
                                            
                                            <Typography variant="subtitle1" gutterBottom>
                                                <strong>Program Breakdown</strong>
                                            </Typography>
                                            
                                            {selectedInvoiceData.records.map((record) => (
                                                record.program_breakdown && record.program_breakdown.length > 0 && (
                                                    <div key={record.id} style={{ marginBottom: 16 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            {getOrganizationDisplay(record.org_code)}
                                                        </Typography>
                                                        
                                                        {record.program_breakdown.map((program, index) => (
                                                            <Paper key={index} className={classes.programCard}>
                                                                <div className={classes.programHeader}>
                                                                    <div>
                                                                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                                                                            {program.module_code}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            {program.user_group}
                                                                        </Typography>
                                                                    </div>
                                                                    <div className={classes.programStats}>
                                                                        <div className={classes.programStat}>
                                                                            <Typography className={classes.programStatValue}>
                                                                                {program.dqp_count || 0}
                                                                            </Typography>
                                                                            <Typography className={classes.programStatLabel}>
                                                                                DQP
                                                                            </Typography>
                                                                        </div>
                                                                        {showDQW && (
                                                                            <div className={classes.programStat}>
                                                                                <Typography className={classes.programStatValue}>
                                                                                    {program.dqw_count || 0}
                                                                                </Typography>
                                                                                <Typography className={classes.programStatLabel}>
                                                                                    DQW
                                                                                </Typography>
                                                                            </div>
                                                                        )}
                                                                        <div className={classes.programStat}>
                                                                            <Typography className={classes.programStatValue}>
                                                                                {program.no_of_users || 0}
                                                                            </Typography>
                                                                            <Typography className={classes.programStatLabel}>
                                                                                Users
                                                                            </Typography>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Paper>
                                                        ))}
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Fade>
                </Modal>
            </CardContent>
        </Card>
    );
}

export default InsiderCard;
